import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useResponsive } from './useResponsive';
import {
  IconShield, IconHistory, IconUser, IconLogout, IconChevron,
} from './Icons';

// ============================================================
// HOME HEADER — shared chrome for the launcher-family pages
// (root landing + /supplychain analyze view): wordmark, case-file
// line, US flag, agent-count badge, and user menu.
// ============================================================

const MONO = "'IBM Plex Mono', 'Courier New', monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const AGENT_COUNT = '30+';

interface HomeHeaderUser {
  email: string;
  is_admin: boolean;
}

interface HomeHeaderProps {
  user: HomeHeaderUser;
  onLogout: () => void;
}

export function firstNameFromEmail(email: string): string {
  const raw = (email.split('@')[0] || '').split(/[._-]/)[0] || '';
  if (!raw) return 'there';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

// Small concentric-circle "target" mark (agent-count badge)
const TargetMark = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <circle cx="12" cy="12" r="5.5" stroke={color} strokeWidth="2" />
    <circle cx="12" cy="12" r="1.6" fill={color} />
  </svg>
);

export default function HomeHeader({ user, onLogout }: HomeHeaderProps) {
  const { T } = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const userMenuItems = [
    { id: 'my-account', path: '/my-account', label: 'My Account', icon: <IconUser /> },
    { id: 'my-attestations', path: '/my-attestations', label: 'My Attestations', icon: <IconShield /> },
    ...(user.is_admin ? [{ id: 'admin', path: '/admin', label: 'Admin Dashboard', icon: <IconHistory /> }] : []),
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr auto' : '1fr auto 1fr',
      alignItems: 'center', gap: 16,
      padding: isMobile ? '16px 20px' : '18px 40px',
      borderBottom: `1px solid ${T.borderCard}`,
    }}>
      <style>{`
        .hh-menu-item:hover { background: ${T.offWhite}; }
      `}</style>

      {/* Left: wordmark + case-file line */}
      <div
        onClick={() => navigate('/')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') navigate('/'); }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, cursor: 'pointer', justifySelf: 'start' }}
      >
        <img src="/tradespy-logo-text.png" alt="TheTradeSpy.ai" style={{ height: 30, width: 'auto' }} />
        {!isMobile && (
          <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em', color: T.textMid, textTransform: 'uppercase' }}>
            Case File TS-2026-001 · Supply Chain Intelligence
          </div>
        )}
      </div>

      {/* Center: US flag */}
      {!isMobile && (
        <div style={{ justifySelf: 'center' }}>
          <img src="/us-flag.png" alt="United States Flag" style={{ height: 44, objectFit: 'contain', border: `1px solid ${T.border}` }} />
        </div>
      )}

      {/* Right: agent-count badge + user menu */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, justifySelf: 'end' }}>
        <button
          onClick={() => navigate('/agent-activity')}
          title={`${AGENT_COUNT} autonomous agents operational`}
          style={{
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 38, height: 38, borderRadius: '50%',
            border: `1px solid ${T.borderCard}`, background: T.surfaceCard,
            cursor: 'pointer',
          }}
        >
          <TargetMark color={T.red} />
          <span style={{
            position: 'absolute', top: -6, right: -6,
            minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9,
            background: T.red, color: '#fff',
            fontFamily: MONO, fontSize: 9.5, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{AGENT_COUNT}</span>
        </button>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 9,
              padding: '6px 10px', borderRadius: 10,
              border: `1px solid ${T.borderCard}`, background: T.surfaceCard,
              cursor: 'pointer', color: T.ink,
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: 7, background: T.ink, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: MONO, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            }}>
              {firstNameFromEmail(user.email).slice(0, 2)}
            </span>
            {!isMobile && (
              <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: T.ink }}>
                {firstNameFromEmail(user.email)}
              </span>
            )}
            <span style={{ color: T.textMid, transform: 'rotate(90deg)', display: 'flex' }}><IconChevron /></span>
          </button>

          {menuOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: T.white, borderRadius: T.radius,
              border: `1px solid ${T.border}`, boxShadow: T.shadowLg,
              minWidth: 220, padding: '8px 0', zIndex: 200,
            }}>
              <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{user.email}</div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{user.is_admin ? 'Administrator' : 'User'}</div>
              </div>
              {userMenuItems.map(item => (
                <button
                  key={item.id}
                  className="hh-menu-item"
                  onClick={() => { setMenuOpen(false); navigate(item.path); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', border: 'none', background: 'transparent',
                    color: T.textMid, cursor: 'pointer', width: '100%', textAlign: 'left',
                    fontSize: 13, fontWeight: 500, fontFamily: SANS,
                  }}
                >
                  <span style={{ color: T.textLight, flexShrink: 0 }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
              <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 4, paddingTop: 4 }}>
                <button
                  className="hh-menu-item"
                  onClick={onLogout}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', border: 'none', background: 'transparent',
                    color: T.danger, cursor: 'pointer', width: '100%', textAlign: 'left',
                    fontSize: 13, fontWeight: 500, fontFamily: SANS,
                  }}
                >
                  <IconLogout />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
